// frontend/src/pages/WithdrawalForm.js
//
// The model withdrawal form.
//
// Annex I(B) of Directive 2011/83/EU is not guidance: the trader must supply
// this form, in this shape, as part of the pre-contractual information. It did
// not exist anywhere in the product — the Terms described the right in prose and
// stopped there, which is one of the omissions that extends the withdrawal
// period by twelve months (Art. 10).
//
// It is a page rather than a PDF so it can carry the trader's details from
// configuration, be linked from the Terms, from the checkout and from the
// contract-confirmation email, and be copied in one tap on a phone. Using it is
// never mandatory — any unequivocal statement withdraws the contract, and the
// one-click self-cancel on the rental page does the same thing faster — so the
// page says that too.
import React, { useEffect, useState } from 'react';
import { Link } from '../routing';
import { Sparkles, Copy } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import toast from 'react-hot-toast';
import { useCompany } from '../utils/useCompany';
import PageHero from '../components/PageHero';
import theme from '../theme';

const LINK = { color: theme.gold };

export default function WithdrawalForm() {
  const { t } = useTranslation();
  const company = useCompany();
  const [copied, setCopied] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // The addressee block is the trader's identity as published — the same source
  // the Terms and the invoices use, so the form is never addressed to a mailbox
  // that has stopped existing.
  const addressee = [
    company.legalName || company.tradingName,
    company.address,
    company.email,
    company.phone,
  ].filter(Boolean).join('\n');

  const formText = [
    t('withdrawal.to'),
    addressee,
    '',
    t('withdrawal.bodyLine'),
    '',
    t('withdrawal.fieldOrdered'),
    t('withdrawal.fieldReference'),
    t('withdrawal.fieldName'),
    t('withdrawal.fieldAddress'),
    t('withdrawal.fieldSignature'),
    t('withdrawal.fieldDate'),
  ].join('\n');

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(formText);
      setCopied(true);
      toast.success(t('withdrawal.copied'));
    } catch (_) {
      // Clipboard permission can simply be refused. The text is on screen and
      // selectable, so say that instead of claiming a copy that did not happen.
      toast.error(t('withdrawal.copyFailed'));
    }
  };

  return (
    <div style={{ fontFamily: theme.font.body, background: theme.cream, minHeight: '100vh' }}>
      <nav style={{ background: theme.espresso, padding: '20px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
          <Sparkles size={18} color={theme.gold} />
          <span style={{ fontFamily: theme.font.display, fontSize: 24, fontWeight: 600, color: theme.white, letterSpacing: '0.05em' }}>
            GLAM<span style={{ color: theme.gold }}>GO</span>
          </span>
        </Link>
        <Link to="/terms" style={{ color: theme.alpha.white(0.6), textDecoration: 'none', fontSize: 12, letterSpacing: '0.12em' }}>
          {t('withdrawal.backToTerms')}
        </Link>
      </nav>

      <PageHero eyebrow={t('withdrawal.label')} title={t('withdrawal.title')} height={260} />

      <div style={{ maxWidth: 760, margin: '0 auto', padding: '56px 24px' }}>
        <div style={{ color: theme.ink600, fontSize: 15, lineHeight: 1.85, marginBottom: 40 }}>
          <p><Trans i18nKey="withdrawal.intro1" components={{ b: <strong /> }} /></p>
          <p>
            <Trans i18nKey="withdrawal.intro2" components={{
              b: <strong />,
              rentals: <Link to="/my-rentals" style={LINK} />,
            }} />
          </p>
          <p><Trans i18nKey="withdrawal.intro3" components={{ b: <strong /> }} /></p>
        </div>

        {/* The form itself, verbatim in the Annex I(B) shape. */}
        <div style={{ background: theme.white, border: `1px solid ${theme.sand}`, borderRadius: 4, padding: '32px 28px' }}>
          <pre style={{
            whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit',
            fontSize: 14, lineHeight: 1.9, color: theme.ink800, margin: 0,
          }}>{formText}</pre>
        </div>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 24 }}>
          <button onClick={copy} style={{
            background: theme.ink, color: theme.white, border: 'none', padding: '13px 26px',
            borderRadius: 2, fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase',
            cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 8,
          }}>
            <Copy size={14} /> {copied ? t('withdrawal.copiedShort') : t('withdrawal.copyForm')}
          </button>
          <a
            href={`mailto:${company.email}?subject=${encodeURIComponent(t('withdrawal.mailSubject'))}&body=${encodeURIComponent(formText)}`}
            style={{
              border: `1px solid ${theme.ink}`, color: theme.ink, padding: '13px 26px', borderRadius: 2,
              fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', textDecoration: 'none',
              display: 'inline-flex', alignItems: 'center',
            }}
          >
            {t('withdrawal.sendByEmail')}
          </a>
        </div>

        <p style={{ color: theme.grey500, fontSize: 13, lineHeight: 1.8, marginTop: 32 }}>
          {t('withdrawal.refundNote')}
        </p>
      </div>

      <footer style={{ background: theme.inkDeep, padding: '40px 24px', color: theme.alpha.white(0.4), fontSize: 13, textAlign: 'center' }}>
        <Link to="/terms" style={{ color: theme.alpha.white(0.4), textDecoration: 'none', margin: '0 16px' }}>{t('terms.termsOfService')}</Link>
        <Link to="/privacy" style={{ color: theme.alpha.white(0.4), textDecoration: 'none', margin: '0 16px' }}>{t('terms.privacyPolicy')}</Link>
        <Link to="/contact" style={{ color: theme.alpha.white(0.4), textDecoration: 'none', margin: '0 16px' }}>{t('terms.contactLink')}</Link>
      </footer>
    </div>
  );
}
